import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import LoadingIcon from "../../../components/Icon/LoadingIcon";
import { formatDateTime } from "../../../../utils/DateUtils";
import { errorAlert } from "../../../components/alert/defaultAlert";

// Traduz os eventos conhecidos dos provedores para que o histórico explique a
// origem da mudança sem expor identificadores técnicos ao usuário.
const EVENT_LABELS = {
    MANUAL_SYNC: "manual_sync",
    CELCOIN_WEBHOOK: "celcoin_webhook",
    ASAAS_WEBHOOK: "asaas_webhook",
    // Baixa registrada no Colmeia (SubscriptionChargeService#saveOutsidePaymentAudit); o
    // payload traz a forma e quem registrou.
    OUTSIDE_PAYMENT: "outside_payment_registered",
    "transaction.updateStatus": "celcoin_transaction_status_updated",
    "subscription.addTransaction": "celcoin_subscription_transaction_added"
};

/**
 * Timeline de mudanças de status de uma SubscriptionCharge (aba "Histórico" do
 * modal de detalhes). Só busca quando `active` vira true - a aba de Detalhes é
 * a padrão, então não faz sentido pagar essa chamada em toda abertura do modal.
 */
const ChargeHistoryTimeline = (props) => {
    const { chargeId, active, getStatusLabel, getStatusTone } = props;
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [openPayloadIndex, setOpenPayloadIndex] = useState(null);

    useEffect(() => {
        if (!active || !chargeId || rows !== null) {
            return;
        }

        setShowLoading(true);
        axiosInstance.get(`${Api.subscription.charge}/${chargeId}/history`)
            .then(resp => setRows(resp.data))
            .catch(error => {
                errorAlert(error);
                setRows([]);
            })
            .finally(() => setShowLoading(false));
    }, [active, chargeId, rows]);

    const eventLabel = (notification) => {
        if (!notification) {
            return "-";
        }
        const key = EVENT_LABELS[notification];
        return key ? t(key) : notification;
    };

    if (showLoading || rows === null) {
        return (
            <div className="charge-history">
                <div className="charge-history__loading">
                    {showLoading ? <LoadingIcon className="fa-2x" /> : null}
                </div>
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="charge-history">
                <div className="charge-history__empty">
                    <p>{t("charge_history_empty")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="charge-history">
            <div className="charge-history__summary">
                <div className="charge-history__summary-card">
                    <span>{t("history")}</span>
                    <strong>{rows.length}</strong>
                </div>
                <div className="charge-history__summary-card">
                    <span>{t("status")}</span>
                    <strong>{t(getStatusLabel(rows[0]?.currentStatus))}</strong>
                </div>
            </div>

            <div className="charge-history__timeline">
                {rows.map((row, index) => (
                    <div
                        key={`${row.date}-${row.currentStatus}-${index}`}
                        className={`charge-history__item charge-history__item--${getStatusTone(row.currentStatus)}`}
                    >
                        <div className="charge-history__rail">
                            <span className="charge-history__dot" />
                            <span className="charge-history__line" />
                        </div>

                        <div className="charge-history__content">
                            <div className="charge-history__head">
                                <span className="charge-history__date">{formatDateTime(row.date)}</span>
                                <span className={`subscription-workspace__status-badge subscription-workspace__status-badge--${getStatusTone(row.currentStatus)}`}>
                                    {t(getStatusLabel(row.currentStatus))}
                                </span>
                            </div>

                            <p className="charge-history__transition">
                                {row.previousStatus
                                    ? `${t(getStatusLabel(row.previousStatus))} → ${t(getStatusLabel(row.currentStatus))}`
                                    : t(getStatusLabel(row.currentStatus))}
                            </p>
                            <p className="charge-history__event">{eventLabel(row.notification)}</p>

                            {row.payload && (
                                <details
                                    className="charge-history__payload"
                                    open={openPayloadIndex === index}
                                    onToggle={(e) => setOpenPayloadIndex(e.target.open ? index : null)}
                                >
                                    <summary>{t("view_payload")}</summary>
                                    <pre>{formatPayload(row.payload)}</pre>
                                </details>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// O payload é salvo como string JSON crua - tenta reformatar pra leitura,
// mas cai no valor original se não for um JSON válido.
const formatPayload = (payload) => {
    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
        return payload;
    }
};

export default ChargeHistoryTimeline;
